/**
 * @Description 标准版本-机会点详情-顶部
 */
import { FC, useEffect, useRef, useState, useMemo, useContext } from 'react';
import { Space, message, Typography } from 'antd';
import styles from '../index.module.less';
import IconFont from '@/common/components/IconFont';
import { useMethods } from '@lhb/hook';

import {
  importChancePoint,
  exportTemplateChancePoint,
  importChancePointStatus,
  importChancePointRecords,
  chancePointRecovery,
} from '@/common/api/expandStore/chancepoint';
import { getApprovalDetail, revokeApproval } from '@/common/api/expandStore/approveworkbench';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import V2Operate from '@/common/components/Others/V2Operate';
import V2Title from '@/common/components/Feedback/V2Title';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import { downloadFile, isArray, isNotEmpty, isNotEmptyAny, refactorPermissions, replaceEmpty } from '@lhb/func';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2ImportModal from '@/common/components/business/V2ImportModal';
import ApprovalNodes from '@/common/components/business/ExpandStore/ApprovalNodes';
import ApprovalCreateDialog from '@/common/components/business/ExpandStore/ApprovalCreateDialog';
import ApproverDetailDrawer from '@/views/expandstore/pages/approver/components/DetailDrawer';
import { ChangePonitDetailType } from '../type';
import { ApprovalStatus, ApprovalType, ApprovalTypeValue, ChanceDetailPermission } from '@/common/components/business/ExpandStore/ts-config';
import AreaProtectionDrawer from '../../../AreaProtection/detail';
import { useBigdataFn } from '@/common/utils/bigdata';
import { formDataComsToTiled } from '../../../DynamicComponent/config';
import { JNPIdentification } from '../../../DynamicComponent/components/CalculationJNP/ts-config';
import { checkContractSign } from '@/common/api/expandStore/shop';
import ShopEditDrawer from '@/views/expandstore/pages/shopEdit/Drawer';
import ChanceDiscardModal, { ChanceDiscardData } from '@/common/components/business/ExpandStore/ChanceDiscardModal';
import UserInfoContext from '@/layout/context';
import Export from './Export';
import CheckContractModal from './CheckContractModal';
// import HintModal from '@/common/components/Pdf/HintModal';

/** 组件传递参数类型 */
interface TitleProps {
  detail: ChangePonitDetailType;
  setFormDrawerData;
  canEditForm: boolean; // 是否可编辑机会点详情表单
  onClose; // 关闭
  updateHandle; // 刷新详情
  saveHandle;
  onSearch?: Function; // 刷新外层列表
  onSearchCurrentPage?: Function; // 刷新外层列表当前页
  hintStr?: string;
  hideOperate? :boolean;
  isRecycle?: boolean;
  titleHeightRef: any;
}

const { Text } = Typography;

/** 标题/操作/审批 */
const Title: FC<TitleProps> = ({
  detail,
  setFormDrawerData,
  canEditForm,
  onClose,
  updateHandle,
  saveHandle,
  onSearch,
  onSearchCurrentPage,
  hintStr,
  hideOperate = false,
  isRecycle = false,
  titleHeightRef,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const selfRef: any = useRef();
  const contractSignRef: any = useRef();
  const [disableCreateApproval, setDisableCreateApproval] = useState<boolean>(false); // 禁用提交审批按钮，提示过一次先区域保护后禁用
  const [showImportModal, setShowImportModal] = useState<boolean>(false); // 是否显示导入弹窗
  const templateAssets = [
    {
      // 数据占位符
      url: 'https://staticres.linhuiba.com/project-custom/locationpc/file/开店计划导入模板.xlsx',
      name: '机会点模板.xlsx',
    },
  ];
  const [importModalWidth, setimportModalWidth] = useState<number>(340); // 导入组件宽度
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [approvalDetail, setApprovalDetail] = useState<any>({});
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false);
  const [approvalPropertyValues, setApprovalPropertyValues] = useState<any>([]);
  // const [showHintModal, setShowHintModal] = useState<boolean>(false);
  const [areaProtectOpen, setAreaProtectOpen] = useState<boolean>(false);
  const [discardData, setDiscardData] = useState<ChanceDiscardData>({});
  const [isLock, setIsLock] = useState<boolean>(false);
  const [contractApprovalData, setContractApprovalData] = useState<any>({
    id: null,
    open: false,
  });
  const { tenantInfo } = useContext(UserInfoContext); // 租户信息
  const [checkContractModalData, setCheckContractModalData] = useState<any>({
    open: false,
    data: null,
  });
  // 是否为基诺浦模板
  const isJNP = useMemo(() => {
    if (!isNotEmptyAny(detail)) return false;

    const { moduleDetails } = detail;
    const module = isArray(moduleDetails) ? moduleDetails.find((itm: any) => itm.moduleType === 4) : null;
    if (!module || !isNotEmptyAny(module.infoModule)) return false;
    const { propertyGroupVOList } = module.infoModule || {};
    if (!isArray(propertyGroupVOList) || !propertyGroupVOList.length) return false;

    const allComs: any = [];
    formDataComsToTiled(propertyGroupVOList, allComs); // 将组件对象平铺到数组中
    const property = allComs?.find(itm => itm.identification === JNPIdentification);
    return isNotEmptyAny(property);
  }, [detail]);

  /**
   * @description 是否在提交审批前进行必须区域保护，且未提交区域保护
   * @return true
   */
  const isNoTaskModeWaitProtect = useMemo(() => {
    return !tenantInfo?.taskTemplateEnable && tenantInfo?.protectFlag && !detail?.protectInfo;
  }, [tenantInfo, detail]);

  /**
   * @description 是否在提交审批前进行必须区域保护，且区域保护还在申请中
   * @return true
   */
  const isNoTaskModeProtectApplying = useMemo(() => {
    return !tenantInfo?.taskTemplateEnable && tenantInfo?.protectFlag && detail?.protectInfo?.statusName && detail?.protectInfo?.statusName !== '保护中';
  }, [tenantInfo, detail]);

  const methods = useMethods({
    /* 点击 导入审批表 */
    async handleImport() {
      // 判断有没有上传历史
      await methods.getHistoy();
      setShowImportModal(true);
    },

    /*
      点击 编辑
      可在详情页内直接编辑表单时，保存表单；页面内不可编辑时，去编辑页
    */
    handleEdit() {
      bigdataFn('4482ac55-09de-4a43-9aee-30d86eac741c', '机会点详情-保存', '', { op_block: '机会点管理', op_event: ``, });
      if (canEditForm) {
        // saveHandle入参分别为needCheck，成功回调
        // 最终方法saveHandle在 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx
        saveHandle(false, () => {
          V2Message.success('保存成功');
          onClose();
        });
        return;
      }
      setFormDrawerData({
        id: detail.id,
        open: true,
        templateId: '',
      });
    },

    /** 点击发起审批 */
    async handleSubmitApproval() {
      bigdataFn('38047dca-f7bd-4f05-bced-6d5eed4eac34', '机会点详情-提交审批', '', { op_block: '机会点管理', op_event: ``, });
      if (isNoTaskModeWaitProtect || isNoTaskModeProtectApplying) {
        if (!disableCreateApproval) { // 提示一次后禁用
          V2Message.warning(isNoTaskModeProtectApplying ? '请等待区域保护申请通过' : '请先提交区域保护');
          setDisableCreateApproval(true);
        }
        return;
      }
      if (canEditForm) {
        // saveHandle入参分别为needCheck，成功回调
        // 最终方法saveHandle在 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx
        saveHandle(true, (propertyValues: any) => {
          // 机会点保存成功后，执行发起处理
          setApprovalPropertyValues(propertyValues || []);
          setApprovalVisible(true);
        });
      } else {
        setApprovalVisible(true);
      }
    },

    /** 点击撤销审批 */
    async handleRevokeApproval() {
      // 一个机会点可能会被多个审批节点绑定，但是只有一项在审批中，撤销审批只撤销在【审批中 item.approvalStatus === 0】的审批
      const approvalingItem = detail?.approvalInfos.find(element => element.approvalStatus === 0); // 正在审批中的元素

      if (approvalingItem) {
        const { approvalId, approvalType, approvalTypeValue } = approvalingItem || {};
        revokeApproval({
          id: approvalId,
          relationType: approvalType,
          typeValue: approvalTypeValue,
        }).then(() => {
          V2Message.success('撤销审批成功');
          onSearch?.();
          onClose();
        }).catch(() => {
          V2Message.success('撤销审批失败，请稍后再试');
        });
      }
    },

    // 门店签约
    handleContractApply: async() => {
      let isPass = false;
      try {
        const params = {
          chancePointId: detail.id,
        };
        const res = await checkContractSign(params);
        const { allow, errorCodeList } = res || {};
        isPass = allow;
        !allow && isArray(errorCodeList) && errorCodeList.length && setCheckContractModalData({
          open: true,
          data: errorCodeList
        });
      } catch (error) {}
      if (!isPass) {
        // 不允许继续流程了
        return;
      }
      const { approvalId, approvalStatus } = detail?.contractApprovingInfo || {};
      if (approvalId && approvalStatus === ApprovalStatus.Untreated) {
        // 签约审批中
        setContractApprovalData({
          id: approvalId,
          open: true,
        });
      } else {
        // 还未生成审批，则允许发起审批，或直接签约通过
        contractSignRef.current.init(detail.id, 2);
      }
    },

    // 废弃
    handleRecycle() {
      setDiscardData({
        id: detail.id,
        name: detail.name,
        open: true,
      });
    },

    // 恢复
    handleRecovery() {
      V2Confirm({
        content: '是否确认重新启用',
        onOk: () => {
          this.confirmRecovery();
        },
      });
    },
    confirmRecovery() {
      if (isLock) return;
      setIsLock(true);
      chancePointRecovery({ chancePointId: detail.id }).then(() => {
        V2Message.success('操作成功');
        onSearchCurrentPage?.(); // 为了请求后继续浏览当前页
        onClose();
      }).finally(() => {
        setIsLock(false);
      });
    },

    // 获取模板
    async getTemplateAssets() {
      const { url } = await exportTemplateChancePoint({ id: detail?.id });
      if (!url) return;
      // url && setTemplateAssets([{
      //   url,
      //   name: '机会点模板.xlsx'
      // }]);
      downloadFile({
        url,
        name: '机会点模板.xlsx',
      });
    },

    // 自定义逻辑
    async customUploadFetch(values, callbackFn) {
      const target = values?.url?.[0];
      if (!target) return;
      const { url, name } = target;
      const urlStr = `${url}?attname=${name}`;
      const params = { url: urlStr, excelName: name, chancePointId: detail.id };
      // importCheck(param);
      try {
        const result = await importChancePointStatus(params);
        if (result?.status === 1) {
          message.warn('当前导入的数据和上一版数据完全一致，请确认导入数据是否正确');
          callbackFn({ isError: true });
        } else {
          V2Confirm({
            onSure: () => {
              methods.importFile(params, callbackFn);
            },
            content: '当前导入的数据和上一版数据不一致，请确认导入数据是否正确',
            zIndex: 1003,
          });
        }
      } catch (e) {
        callbackFn({ isError: true });
      }
    },

    /** 上传文件 */
    importFile(params: any, callbackFn) {
      importChancePoint(params).then(
        () => {
          message.success('导入完成');
          callbackFn && callbackFn();
          updateHandle();
        },
        () => {
          callbackFn && callbackFn({ isError: true });
        }
      );
    },

    /** 获取导入历史 */
    getHistoy() {
      importChancePointRecords({ id: detail.id }).then((data: any) => {
        if (isArray(data) && data.length) {
          setHistoryData(data.splice(0, 5)); // 展示最近的5条
          setimportModalWidth(720);
          return;
        }
        setHistoryData([]);
      });
    },

    /** 获取审批详情 */
    getApprovalDetailData(target: any) {
      const { approvalId } = target;
      getApprovalDetail({ id: approvalId }).then((data) => {
        if (data && isNotEmpty(data.status) && isNotEmpty(data.statusName)) { // 审批撤销的情况下status与statusName为空，撤销情况下不需要展示审批信息
          setApprovalDetail(data);
        } else {
          setApprovalDetail({});
        }
      }).finally(() => {
        methods.getHeight();
      });
    },

    /**
     * 获取高度
     */
    getHeight: () => {
      setTimeout(() => {
        titleHeightRef.current = selfRef.current?.getBoundingClientRect()?.height || 0;
      }, 0);
    },
    /**
     * 点击区域保护按钮
     */
    handleSubmitProtect() {
      setAreaProtectOpen(true);
    },
    /**
     * @description 对权限数组设置主按钮（type属性，主按钮'primary'，其他为'default'）
     * @param perms 权限数组
     * @return 处理后的权限数组
     */
    dealHighlightButton(perms: any[]) {
      /*
        设置主功能按钮，
        有编辑按钮，编辑按钮作为主按钮
        无编辑按钮，默认将最后一个按钮蓝底高亮。遇到不可作为主功能的例外功能（如废弃）则跳过将倒数第二个设置为高亮
      */
      // 判断是否有编辑按钮
      const hasEditPerm = !!perms.find((item: any) => item.event === ChanceDetailPermission.Edit);
      if (hasEditPerm) {
        // 有编辑按钮
        const res = perms.map((item) => ({
          ...item,
          // 编辑按钮 蓝色
          type: item.event === ChanceDetailPermission.Edit ? 'primary' : 'default',
        }));
        return res;
      }

      const len = perms.length;
      const notMainEvents = [ChanceDetailPermission.Recycle]; // 不显示为主功能的按钮事件
      let hasSetMain = false; // 是否已设置过主功能按钮
      const res: any = [];
      for (let i = len - 1; i >= 0; i--) {
        const item = perms[i];
        if (!hasSetMain && !notMainEvents.includes(item.event)) {
        // 找到了要设置为主功能的按钮
          item.type = 'primary';
          hasSetMain = true;
        } else {
          item.type = 'default';
        }
        res.push(item);
      }
      res.reverse();
      return res;
    },
    /**
     * @description 对权限按钮排序，主按钮排到最后
     * @param perms 权限数组
     * @return 处理后的权限数组
     */
    sortHighlightButton(perms: any[]) {
      const defaultBtns = perms.filter(item => item.type !== 'primary');
      const primaryBtns = perms.filter(item => item.type === 'primary');
      const res = [...defaultBtns, ...primaryBtns];
      return res;
    }
  });

  /**
   * @description 按钮权限列表
   * @return
   *   特殊处理
   *     a.如果可以编辑表单，将edit权限替换为保存按钮
   *     b.针对提交审批按钮，租户未开启任务模板+已开启区域保护+机会点未完成区域保护申请+已经点击提交审批并taost提示过，按钮禁用置灰
   */
  const operateList = useMemo(() => {
    if (!Array.isArray(detail?.permissions)) return [];

    let refactorPerms = refactorPermissions(detail.permissions);
    // 如果支持页面内编辑，将编辑按钮标题改为保存
    if (canEditForm) {
      refactorPerms = refactorPerms.map((itm: any) => itm.event === ChanceDetailPermission.Edit ? { ...itm, name: '保存' } : itm);
    }
    // 提交审批按钮有些情况下需要设置为禁用
    refactorPerms = refactorPerms.map((itm: any) => itm.event === ChanceDetailPermission.SubmitApprove ? { ...itm, disabled: disableCreateApproval } : itm);
    // 过滤掉导出报告按钮，另外单独展示
    refactorPerms = refactorPerms.filter((itm: any) => itm.event !== ChanceDetailPermission.ReportExport);

    // 设置主按钮
    let res = methods.dealHighlightButton(refactorPerms);
    // 将主按钮移到最后
    res = methods.sortHighlightButton(res);
    return res;
  }, [detail, disableCreateApproval]);

  // 编辑/导入权限
  useEffect(() => {
    // 判断是否有审批
    const { approvalInfos } = detail || {};
    if (isArray(approvalInfos) && approvalInfos.length) {
      const target = approvalInfos[0]; // 目前只有一种类型只需要取第一个就行
      target && methods.getApprovalDetailData(target);
    } else {
      setApprovalDetail({}); // 重置审批信息
    }
    methods.getHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  return detail ? (
    <div ref={selfRef} className={styles.detailTop}>
      {/* 点位名称以及操作按钮，废弃机会点只展示重新启用按钮 */}
      <V2Title
        extra={
          (
            isRecycle ? <V2Operate
              operateList={operateList}
              onClick={(btn) => methods[btn.func]()}
              showBtnCount={5}
            /> : !hideOperate ? <div>
              <Space>
                <V2Operate
                  operateList={operateList}
                  onClick={(btn) => methods[btn.func]()}
                  showBtnCount={5}
                />
                <Export
                  detail={detail}
                  approvalDetail={approvalDetail}
                />
              </Space>
            </div> : <></>)
        }
      >
        <Text
          style={ { width: '100%' }}
          ellipsis={{ tooltip: detail.name }}
          className={styles.spotName}
        >
          {replaceEmpty(detail.name)}
        </Text>
      </V2Title>
      { !!isJNP &&
        <Text
          style={ { width: '100%' }}
          ellipsis={{ tooltip: detail.address }}
          className={styles.spotAddress}
        >
          <IconFont iconHref='iconIC_dizhi' className={styles.spotAddressIcon}></IconFont>
          {replaceEmpty(detail.address)}
        </Text>
      }

      <div className='c-f23'>{hintStr}</div>

      <V2ImportModal
        isOpen={showImportModal}
        closeModal={() => setShowImportModal(false)}
        title='导入机会点'
        modalWidth={importModalWidth}
        templateAssets={templateAssets}
        firstStepTemplateContent={
          <div className={styles.item}>
            <V2DetailItem
              type='files'
              filePreviewHide
              fileDownloadHide
              assets={templateAssets}
              rightSlot={{
                icon: <IconFont iconHref='icondownload' className='c-006' />,
                onIconClick: () => methods.getTemplateAssets(),
              }}
            />
          </div>
        }
        rightSlot={
          historyData?.length > 0 ? (
            <>
              {historyData.map((item: any, index: number) => (
                <V2DetailItem
                  key={index + item.name}
                  type='files'
                  assets={[
                    {
                      url: item.url,
                      name: item.name,
                    },
                  ]}
                />
              ))}
            </>
          ) : null
        }
        customUploadFetch={(values, callbackFn) => methods.customUploadFetch(values, callbackFn)}
      />
      {/* 当含有审批状态的时候渲染 */}
      {
        isNotEmptyAny(approvalDetail) ? <ApprovalNodes detail={approvalDetail} /> : null
      }
      <ApprovalCreateDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        id={detail.id}
        type={ApprovalType.ChancePoint}
        typeValue={ApprovalTypeValue.ChancePointEvaluate}
        specialParams={{
          propertyValues: approvalPropertyValues
        }}
        onComplete={() => {
          onSearch?.();
          onClose();
        }}/>
      {/* 导出弹窗提醒 */}
      {/* <HintModal
        open={showHintModal}
        setOpen={setShowHintModal}
      /> */}
      {/* 区域保护弹窗 */}
      <AreaProtectionDrawer
        id={detail.id}
        open={areaProtectOpen}
        setOpen={setAreaProtectOpen}
        updateHandle={() => {
          setDisableCreateApproval(false); // 更新的时候恢复按钮状态
          updateHandle();
        }}
      />
      {/* 合同签约 */}
      <ShopEditDrawer ref={contractSignRef} reload={() => {
        updateHandle();
        onSearch?.();
      }}/>
      {/* 废弃操作弹框 */}
      <ChanceDiscardModal
        modalData={discardData}
        setModalData={setDiscardData}
        onSuccess={() => {
          onSearchCurrentPage?.(); // 为了请求后继续浏览当前页
          onClose();
        }}
      />
      {/* 审批详情抽屉 */}
      <ApproverDetailDrawer
        id={contractApprovalData?.id}
        open={contractApprovalData?.open} // 是否可见
        setOpen={(op) => {
          setContractApprovalData((state) => ({
            ...state,
            open: op,
          }));
        }}
        onRefresh={() => onSearch?.()} // 刷新列表
      />
      <CheckContractModal
        modalData={checkContractModalData}
        close={() => setCheckContractModalData({
          open: false,
          data: null
        })}
        handleAreaProtection={() => {
          setCheckContractModalData({
            open: false,
            data: null
          });
          setAreaProtectOpen(true);
        }}
      />
    </div>
  ) : null;
};

export default Title;
